import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Modal, Button, Message, Loader, Form } from 'semantic-ui-react';

import Config from '../../../constants/Config';
import selectors from '../../../selectors';
import styles from './AssignFormsModal.module.scss';

const AssignFormsModal = React.memo(({ cardId, onClose, onSuccess }) => {
  const accessToken = useSelector(selectors.selectAccessToken);
  const [availableForms, setAvailableForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadForms = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/inspection-forms?includeTemplates=true`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailableForms(data.items || []);
      } else {
        setError('Failed to load forms');
      }
    } catch (err) {
      setError('Error loading forms');
      console.error('Error loading forms:', err);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const handleSave = useCallback(async () => {
    setError(null);

    if (!selectedForm) {
      setError('Please select a form');
      return;
    }

    setIsSaving(true);

    try {
      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/cards/${cardId}/forms/${selectedForm}/assign`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (response.ok) {
        onSuccess();
        onClose();
      } else {
        setError('Failed to assign form');
      }
    } catch (err) {
      setError('Error assigning form');
      console.error('Error:', err);
    } finally {
      setIsSaving(false);
    }
  }, [cardId, selectedForm, accessToken, onSuccess, onClose]);

  return (
    <Modal open onClose={onClose} size="small">
      <Modal.Header>Assign Form</Modal.Header>
      <Modal.Content>
        {isLoading ? (
          <div className={styles.loading}>
            <Loader active inline="centered" />
            <p>Loading forms...</p>
          </div>
        ) : (
          <Form>
            {error && (
              <Message negative>
                <Message.Header>Error</Message.Header>
                <p>{error}</p>
              </Message>
            )}

            <Form.Field>
              <label>Select Form</label>
              <select value={selectedForm} onChange={e => setSelectedForm(e.target.value)}>
                <option value="">-- Select Form --</option>
                {availableForms.map(form => (
                  <option key={form.id} value={form.id}>
                    {form.name} {form.isTemplate ? '(Template)' : ''}
                  </option>
                ))}
              </select>
              <small>Select any form to assign to this card</small>
            </Form.Field>

            {availableForms.length === 0 && (
              <Message info>
                <Message.Header>No forms available</Message.Header>
                <p>Create forms in Settings &rarr; Forms before assigning them</p>
              </Message>
            )}
          </Form>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          primary
          onClick={handleSave}
          loading={isSaving}
          disabled={isLoading || !selectedForm}
        >
          Assign Form
        </Button>
      </Modal.Actions>
    </Modal>
  );
});

AssignFormsModal.propTypes = {
  cardId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AssignFormsModal;
